import { useEffect, useState, useRef, useCallback } from "react";
import QRCode from "qrcode";

interface QROverlayProps {
  registerUrl: string;
  remainingSeconds: number;
}

const TimeDisplay = ({ seconds }: { seconds: number }) => {
  const unit = seconds === 1 ? "Sekunde" : "Sekunden";
  return `${seconds} ${unit}`;
};

export function QROverlay({ registerUrl, remainingSeconds }: QROverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrReady, setQrReady] = useState(false);
  const [seconds, setSeconds] = useState(remainingSeconds);

  useEffect(() => {
    if (canvasRef.current && registerUrl) {
      QRCode.toCanvas(canvasRef.current, registerUrl, {
        width: 280,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      })
        .then(() => setQrReady(true))
        .catch((err) => console.error("QR code generation failed:", err));
    }
  }, [registerUrl]);

  useEffect(() => {
    setSeconds(remainingSeconds);
    const interval = window.setInterval(() => {
      setSeconds((state) => {
        return state > 0 ? state - 1 : 0;
      });
    }, 1000);
    return () => {
      window.clearInterval(interval);
    };
  }, [remainingSeconds]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full mx-4 text-center">
        <h2 className="text-4xl font-bold text-black mb-2">
          Gerät registrieren
        </h2>
        <p className="text-black/60 mb-6 text-2xl">
          Um Zugriff auf das Board zu erhalten, scanne den QR-Code mit dem
          Smartphone.
        </p>

        <div className="flex justify-center mb-6">
          <div className="p-4 bg-white rounded-2xl border-2 border-black/10 shadow-inner">
            <canvas
              ref={canvasRef}
              className={`transition-opacity duration-300 ${
                qrReady ? "opacity-100" : "opacity-0"
              }`}
            />
            {!qrReady && (
              <div className="w-[280px] h-[280px] flex items-center justify-center">
                <div className="animate-spin text-4xl">⏳</div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center mb-2 text-2xl text-black/70">
          <span className="font-mono text-4xl uppercase">
            {registerUrl.slice(-8)}
          </span>
        </div>

        <div className="flex items-center justify-center gap-2 text-2xl text-black/70">
          <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
          {seconds > 300 ? (
            <span>Warten auf das erste Gerät ...</span>
          ) : (
            <span>
              Noch{" "}
              <span className="font-bold text-black">
                <TimeDisplay seconds={seconds} />
              </span>{" "}
              aktiv
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
